import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ClockIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,

  ServerIcon,
  PlayIcon,
  StopIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, pitrApi } from '@/lib/api';
import { BaseBackup, PITRRestore } from '@/types';
import toast from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function PITRDashboard() {
  const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<BaseBackup | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'backups' | 'restores'>('overview');

  const [enableConfig, setEnableConfig] = useState({
    base_backup_interval_hours: 24,
    wal_retention_hours: 168,
    max_base_backups: 7,
  });

  const [restoreTime, setRestoreTime] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    { enabled: isAuthenticated && !!projectId }
  );

  // Fetch PITR config
  const { data: pitrResponse, isLoading: pitrLoading } = useQuery(
    ['pitr', projectId],
    () => pitrApi.getConfig(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: 10000,
    }
  );

  // Fetch base backups
  const { data: baseBackupsResponse } = useQuery(
    ['pitr-backups', projectId],
    () => pitrApi.getBaseBackups(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: 10000,
    }
  );

  // Fetch restore history — poll frequently when a restore is in progress
  const { data: restoreHistoryResponse } = useQuery(
    ['pitr-restores', projectId],
    () => pitrApi.getRestoreHistory(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: (data) => {
        const restores = data?.data?.restores || [];
        const hasActive = restores.some((r: PITRRestore) => r.status === 'pending' || r.status === 'in_progress');
        return hasActive ? 3000 : 30000;
      },
    }
  );

  // Enable PITR mutation
  const enableMutation = useMutation(
    (data: typeof enableConfig) =>
      pitrApi.enablePITR(projectId as string, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['pitr', projectId]);
        if (response.success) {
          setShowEnableModal(false);
          toast.success('PITR enabled successfully');
        } else {
          const errMsg = response.data?.error || 'Unknown error';
          toast.error(`Failed to enable PITR: ${errMsg}`, { duration: 8000 });
        }
      },
      onError: () => { toast.error('Failed to enable PITR'); },
    }
  );

  // Disable PITR mutation
  const disableMutation = useMutation(
    () => pitrApi.disablePITR(projectId as string),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pitr', projectId]);
        setShowDisableConfirm(false);
        toast.success('PITR disabled');
      },
      onError: () => { toast.error('Failed to disable PITR'); },
    }
  );

  // Update config mutation
  const updateConfigMutation = useMutation(
    (data: typeof enableConfig) =>
      pitrApi.updateConfig(projectId as string, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pitr', projectId]);
        setShowConfigModal(false);
        toast.success('Configuration updated');
      },
      onError: () => { toast.error('Failed to update configuration'); },
    }
  );

  // Create base backup mutation
  const createBaseBackupMutation = useMutation(
    () => pitrApi.createBaseBackup(projectId as string),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['pitr-backups', projectId]);
        queryClient.invalidateQueries(['pitr', projectId]);
        if (response.success) {
          toast.success('Base backup created successfully');
        } else {
          toast.error('Base backup creation failed');
        }
      },
      onError: () => { toast.error('Failed to create base backup'); },
    }
  );

  // Delete base backup mutation
  const deleteBaseBackupMutation = useMutation(
    (backupId: string) => pitrApi.deleteBaseBackup(backupId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pitr-backups', projectId]);
        queryClient.invalidateQueries(['pitr', projectId]);
        setShowDeleteConfirm(null);
        toast.success('Base backup deleted');
      },
      onError: () => { toast.error('Failed to delete base backup'); },
    }
  );

  // Restore mutation — returns immediately, restore runs in background
  const restoreMutation = useMutation(
    (targetTime: string) =>
      pitrApi.restoreToPointInTime(projectId as string, targetTime),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['pitr-restores', projectId]);
        setShowRestoreModal(false);
        setShowRestoreConfirm(false);
        setRestoreTime('');
        if (response.success) {
          toast.success('Restore initiated — check the Restore History tab for progress', { duration: 6000 });
          setActiveTab('restores');
        } else {
          const errMsg = response.data?.error || 'Unknown error';
          toast.error(`Restore failed: ${errMsg}`, { duration: 10000 });
        }
      },
      onError: (error: unknown) => {
        const axiosErr = error as { response?: { data?: { message?: string; error?: string } } };
        const msg = axiosErr?.response?.data?.message || axiosErr?.response?.data?.error || 'Unknown error';
        toast.error(`Failed to initiate restore: ${msg}`, { duration: 10000 });
      },
    }
  );

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  const pitrData = pitrResponse?.data;
  const config = pitrData?.config;
  const stats = pitrData?.stats;
  const recoveryWindow = pitrData?.recovery_window;
  const baseBackups: BaseBackup[] = baseBackupsResponse?.data?.base_backups || [];
  const restores: PITRRestore[] = restoreHistoryResponse?.data?.restores || [];
  const pitrEnabled = config?.enabled && config?.status === 'active';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'configuring': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress': return 'IN PROGRESS';
      default: return status.toUpperCase();
    }
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Point-in-Time Recovery
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Continuous WAL archiving with point-in-time restore for {project?.name || 'this project'}.
          </p>
        </div>
        {pitrEnabled && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex space-x-2">
            <button
              onClick={() => setShowRestoreModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              Restore to Point in Time
            </button>
            <button
              onClick={() => createBaseBackupMutation.mutate()}
              disabled={createBaseBackupMutation.isLoading}
              className="btn-primary disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {createBaseBackupMutation.isLoading ? 'Creating...' : 'Base Backup'}
            </button>
          </div>
        )}
      </div>

      {pitrLoading ? (
        <div className="mt-8 animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      ) : !pitrEnabled ? (
        /* PITR Not Enabled */
        <div className="mt-8 text-center py-16 card">
          <ShieldCheckIcon className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Point-in-Time Recovery is not enabled
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            Enable PITR to continuously archive WAL (Write-Ahead Log) files and create
            periodic base backups. This allows you to restore your database to any
            point in time within the retention window.
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                Continuous WAL archiving
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                Automated base backups
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                Restore to any second
              </div>
            </div>
            <button
              onClick={() => setShowEnableModal(true)}
              className="btn-primary"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              Enable PITR
            </button>
          </div>
          {config?.status === 'error' && config.error_message && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 max-w-md mx-auto">
              <p className="text-sm text-red-700">
                <strong>Previous error:</strong> {config.error_message}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Status Bar */}
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-800">PITR is Active</p>
                <p className="text-xs text-green-600">
                  WAL archiving is continuous. Base backups every {config?.base_backup_interval_hours}h.
                  Retention: {config?.wal_retention_hours}h ({Math.round((config?.wal_retention_hours || 168) / 24)}d).
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEnableConfig({
                    base_backup_interval_hours: config?.base_backup_interval_hours || 24,
                    wal_retention_hours: config?.wal_retention_hours || 168,
                    max_base_backups: config?.max_base_backups || 7,
                  });
                  setShowConfigModal(true);
                }}
                className="btn-secondary text-sm"
              >
                <Cog6ToothIcon className="h-4 w-4 mr-1" />
                Configure
              </button>
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="btn-secondary text-red-600 hover:text-red-800 text-sm"
              >
                <StopIcon className="h-4 w-4 mr-1" />
                Disable
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card">
              <div className="flex items-center">
                <ServerIcon className="h-8 w-8 text-primary-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Base Backups</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats?.completed_base_backups || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Recovery Window</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {recoveryWindow?.earliest
                      ? formatDistanceToNow(new Date(recoveryWindow.earliest))
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center">
                <ServerIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Size</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatBytes(stats?.total_backup_size_bytes || 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Last Backup</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats?.last_base_backup_at
                      ? formatDistanceToNow(new Date(stats.last_base_backup_at), { addSuffix: true })
                      : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {(['overview', 'backups', 'restores'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`${
                      activeTab === tab
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                  >
                    {tab === 'backups' ? 'Base Backups' : tab === 'restores' ? 'Restore History' : tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="mt-6 space-y-6">
                {/* Recovery Window Visualization */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recovery Window</h3>
                  {recoveryWindow?.earliest ? (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Earliest restore point:</span>{' '}
                          {format(new Date(recoveryWindow.earliest), 'PPpp')}
                        </div>
                        <div>
                          <span className="font-medium">Latest:</span> Now
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-green-500 h-3 rounded-full"
                          style={{ width: '100%' }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500">
                        You can restore to any point within this window. The window covers{' '}
                        {formatDistanceToNow(new Date(recoveryWindow.earliest))} of history.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No base backups available yet. A recovery window will be established once the first base backup completes.
                    </p>
                  )}
                </div>

                {/* Configuration Details */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Base Backup Interval</p>
                      <p className="text-sm text-gray-900 mt-1">
                        Every {config?.base_backup_interval_hours} hours
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">WAL Retention</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {config?.wal_retention_hours} hours ({Math.round((config?.wal_retention_hours || 168) / 24)} days)
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Max Base Backups</p>
                      <p className="text-sm text-gray-900 mt-1">{config?.max_base_backups}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Base Backups Tab */}
            {activeTab === 'backups' && (
              <div className="mt-6">
                {baseBackups.length === 0 ? (
                  <div className="text-center py-12 card">
                    <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No base backups yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      A base backup will be created automatically, or you can create one manually.
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={() => createBaseBackupMutation.mutate()}
                        disabled={createBaseBackupMutation.isLoading}
                        className="btn-primary disabled:opacity-50"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {createBaseBackupMutation.isLoading ? 'Creating...' : 'Create Base Backup'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {baseBackups.map((backup) => {
                      const isInProgress = backup.status === 'in_progress';
                      return (
                        <div key={backup.id} className="card">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                {isInProgress ? (
                                  <ArrowPathIcon className="h-5 w-5 mr-3 animate-spin text-blue-500" />
                                ) : (
                                  <ServerIcon className="h-5 w-5 mr-3 text-gray-400" />
                                )}
                                <div>
                                  <h3 className="text-sm font-medium text-gray-900">
                                    Base Backup
                                  </h3>
                                  <div className="flex items-center mt-1 space-x-4 flex-wrap">
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(backup.status)}`}>
                                      {getStatusLabel(backup.status)}
                                    </span>
                                    {backup.file_size_bytes && (
                                      <span className="text-xs text-gray-500">
                                        Size: {formatBytes(backup.file_size_bytes)}
                                      </span>
                                    )}
                                    {backup.execution_time_ms && (
                                      <span className="text-xs text-gray-500">
                                        Duration: {(backup.execution_time_ms / 1000).toFixed(1)}s
                                      </span>
                                    )}
                                    {backup.wal_start_lsn && (
                                      <span className="text-xs text-gray-500">
                                        LSN: {backup.wal_start_lsn}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                      Created {formatDistanceToNow(new Date(backup.created_at), { addSuffix: true })}
                                    </span>
                                    {backup.expires_at && (
                                      <span className="text-xs text-gray-500">
                                        Expires {formatDistanceToNow(new Date(backup.expires_at), { addSuffix: true })}
                                      </span>
                                    )}
                                  </div>
                                  {backup.error_message && (
                                    <p className="mt-1 text-xs text-red-600">{backup.error_message}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              {!isInProgress && (
                                <button
                                  onClick={() => setShowDeleteConfirm(backup)}
                                  disabled={deleteBaseBackupMutation.isLoading}
                                  className="btn-secondary text-red-600 hover:text-red-800 disabled:opacity-50"
                                  title="Delete base backup"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Restore History Tab */}
            {activeTab === 'restores' && (
              <div className="mt-6">
                {restores.length === 0 ? (
                  <div className="text-center py-12 card">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No restores yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Restore history will appear here when you perform point-in-time restores.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {restores.map((restore) => {
                      const isInProgress = restore.status === 'in_progress';
                      return (
                        <div key={restore.id} className="card">
                          <div className="flex items-center">
                            {isInProgress ? (
                              <ArrowPathIcon className="h-5 w-5 mr-3 animate-spin text-blue-500" />
                            ) : (
                              <ClockIcon className="h-5 w-5 mr-3 text-gray-400" />
                            )}
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">
                                Restore to {format(new Date(restore.target_time), 'PPpp')}
                              </h3>
                              <div className="flex items-center mt-1 space-x-4 flex-wrap">
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(restore.status)}`}>
                                  {getStatusLabel(restore.status)}
                                </span>
                                {restore.execution_time_ms && (
                                  <span className="text-xs text-gray-500">
                                    Duration: {(restore.execution_time_ms / 1000).toFixed(1)}s
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">
                                  Initiated {formatDistanceToNow(new Date(restore.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              {restore.error_message && (
                                <p className="mt-1 text-xs text-red-600">{restore.error_message}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Enable PITR Modal */}
      {showEnableModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Enable Point-in-Time Recovery</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will configure WAL archiving and automated base backups for continuous
                point-in-time recovery capability.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base Backup Interval (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={enableConfig.base_backup_interval_hours}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, base_backup_interval_hours: parseInt(e.target.value) || 24 })
                    }
                    className="mt-1 form-input w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    How often to create full base backups. Recommended: 24h.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    WAL Retention (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="720"
                    value={enableConfig.wal_retention_hours}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, wal_retention_hours: parseInt(e.target.value) || 168 })
                    }
                    className="mt-1 form-input w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    How far back you can restore. 168h = 7 days. Maximum 720h = 30 days.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Base Backups
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={enableConfig.max_base_backups}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, max_base_backups: parseInt(e.target.value) || 7 })
                    }
                    className="mt-1 form-input w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum number of base backups to retain. Oldest will be removed automatically.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button onClick={() => setShowEnableModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => enableMutation.mutate(enableConfig)}
                  disabled={enableMutation.isLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {enableMutation.isLoading ? 'Enabling...' : 'Enable PITR'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configure Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Update PITR Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base Backup Interval (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={enableConfig.base_backup_interval_hours}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, base_backup_interval_hours: parseInt(e.target.value) || 24 })
                    }
                    className="mt-1 form-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    WAL Retention (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="720"
                    value={enableConfig.wal_retention_hours}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, wal_retention_hours: parseInt(e.target.value) || 168 })
                    }
                    className="mt-1 form-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Base Backups
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={enableConfig.max_base_backups}
                    onChange={(e) =>
                      setEnableConfig({ ...enableConfig, max_base_backups: parseInt(e.target.value) || 7 })
                    }
                    className="mt-1 form-input w-full"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button onClick={() => setShowConfigModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => updateConfigMutation.mutate(enableConfig)}
                  disabled={updateConfigMutation.isLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {updateConfigMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Restore to Point in Time</h3>
              {recoveryWindow?.earliest ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Available window:</strong>{' '}
                      {format(new Date(recoveryWindow.earliest), 'PPpp')} to now
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Target Date & Time (UTC)
                    </label>
                    <input
                      type="datetime-local"
                      value={restoreTime}
                      onChange={(e) => setRestoreTime(e.target.value)}
                      className="mt-1 form-input w-full"
                      min={new Date(recoveryWindow.earliest).toISOString().slice(0, 16)}
                      max={new Date().toISOString().slice(0, 16)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Select the exact point in time you want to restore to.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-4 mt-6">
                    <button
                      onClick={() => {
                        setShowRestoreModal(false);
                        setRestoreTime('');
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowRestoreConfirm(true)}
                      disabled={!restoreTime}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
                    >
                      Continue
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-sm text-gray-500">
                    No recovery window available. Wait for the first base backup to complete.
                  </p>
                  <div className="flex justify-end mt-6">
                    <button onClick={() => setShowRestoreModal(false)} className="btn-secondary">
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && restoreTime && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Confirm Restore</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Restore database to:{' '}
                <strong>{format(new Date(restoreTime), 'PPpp')}</strong>
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This will completely replace the current database with
                  the state from the selected point in time. All changes made after that time will
                  be lost. This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowRestoreConfirm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => restoreMutation.mutate(new Date(restoreTime).toISOString())}
                  disabled={restoreMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {restoreMutation.isLoading ? 'Restoring...' : 'Restore Database'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Disable PITR</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Disabling PITR will stop WAL archiving and automated base backups. Existing
                base backups will be retained but no new ones will be created. You will lose
                the ability to restore to a point in time.
              </p>
              <div className="flex justify-end space-x-4">
                <button onClick={() => setShowDisableConfirm(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => disableMutation.mutate()}
                  disabled={disableMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {disableMutation.isLoading ? 'Disabling...' : 'Disable PITR'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Base Backup Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <TrashIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Delete Base Backup</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this base backup? This may reduce your
                available recovery window. This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => deleteBaseBackupMutation.mutate(showDeleteConfirm.id)}
                  disabled={deleteBaseBackupMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {deleteBaseBackupMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
